import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { createApiClient, apiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../api/apiClient';
import { setToastNotification } from '../../../store/slices/standUpSlice';
import { NewsItem } from '../types';
import { AppDispatch } from '../../../store';

interface UseAddNewsParams {
  selectedLocation?: {
    displayText?: string;
    id?: string | number;
  } | null;
  // For standup-specific news operations
  standupId?: string | number | null;
  sessionId?: string | null;
}

interface AddNewsPayload {
  title: string;
  description: string;
  location_name: string;
  is_favourite?: boolean;
  location_id: string;
  source?: 'global' | 'standup';
  standup_id?: string | number;
  session_id?: string;
}

interface ToggleFavoritePayload {
  news_id: number;
  title: string;
  description: string;
  location_name: string;
  location_id: string;
  is_active: boolean;
  is_favourite: boolean;
  standup_id?: string | number;
  session_id?: string;
}

export const useAddNews = ({ selectedLocation, standupId, sessionId }: UseAddNewsParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Add news - for standup context, automatically sets source='global' and is_favourite=true
   */
  const addNews = async (newNews: NewsItem, isStandupContext: boolean = false): Promise<NewsItem | null> => {
    setIsLoading(true);
    try {
      const api = createApiClient();
      const payload: AddNewsPayload = {
        title: newNews.title,
        description: newNews.description,
        location_name: selectedLocation?.displayText || '',
        location_id: selectedLocation?.id?.toString() || '',
      };

      // For standup context, set source and link to standup
      if (isStandupContext) {
        payload.source = 'global';
        payload.is_favourite = true; // Auto-favorite when created in standup
        
        // Use standup_id for existing standups, session_id for new standups
        if (standupId) {
          payload.standup_id = standupId;
        } else if (sessionId) {
          payload.session_id = sessionId;
        }
      } else {
        payload.source = 'global';
        payload.is_favourite = newNews.is_favourite || false;
      }

      // Generate clientTimestamp and clientTimezone for query parameters
      const clientTimezone = getTimezoneOffsetString();
      const url = `/stand-up/news?clientTimezone=${encodeURIComponent(clientTimezone)}`;

      const response = await apiClient(api, url, 'POST', payload);
      
      console.log('News saved response:', response);

      // Handle success response
      if (response?.success === true && response?.data?.id) {
        const updatedNews = {
          ...newNews,
          id: response.data.id.toString(),
          is_favourite: response.data.is_favourite ?? (isStandupContext ? true : newNews.is_favourite),
        };
        return updatedNews;
      }

      // Handle case where success might not be explicitly set but id is returned
      if (response?.data?.id) {
        const updatedNews = {
          ...newNews,
          id: response.data.id.toString(),
          is_favourite: response.data.is_favourite ?? (isStandupContext ? true : newNews.is_favourite),
        };
        return updatedNews;
      }

      throw new Error('No ID returned from API');
    } catch (error: any) {
      console.error('Error saving news:', error);

      let errorMessage = 'Failed to save news';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch(
        setToastNotification({
          header: "Error",
          subtext: errorMessage,
          variant: "error",
          position: "top",
          duration: 6000,
        }),
      );

      return null;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle news favorite - for standup-specific news, unfavoriting will disassociate from standup
   * @param newsId - The ID of the news item
   * @param newsItem - The news item object
   * @returns Object with success status and whether the item was disassociated from standup
   */
  const toggleNewsFavorite = async (newsId: number, newsItem: NewsItem): Promise<{ success: boolean; wasDisassociated: boolean }> => {
    setIsLoading(true);
    try {
      const newFavoriteStatus = !newsItem.is_favourite;
      // Check if news is standup-specific by checking if it has standup_id or session_id
      const currentStandupId = standupId ? parseInt(standupId.toString()) : null;
      const currentSessionId = sessionId || null;
      const isStandupSpecificNews = (newsItem.standup_id === currentStandupId || newsItem.session_id === currentSessionId);
      const isGlobalNews = !newsItem.standup_id && !newsItem.session_id;
      const isUnfavoriting = !newFavoriteStatus;
      const isFavoriting = newFavoriteStatus;
      
      // When unfavoriting standup-specific news, it will be disassociated from standup
      const willBeDisassociated = isStandupSpecificNews && isUnfavoriting;
      
      const payload: ToggleFavoritePayload = {
        news_id: newsId,
        title: newsItem.title,
        description: newsItem.description,
        location_name: selectedLocation?.displayText || "",
        location_id: selectedLocation?.id?.toString() || "",
        is_active: true,
        is_favourite: newFavoriteStatus,
      };

      // When favoriting a global news item, associate it with the current standup
      if (isGlobalNews && isFavoriting) {
        if (standupId) {
          payload.standup_id = standupId;
        } else if (sessionId) {
          payload.session_id = sessionId;
        }
      }

      // When unfavoriting any news, send standup_id (for existing standup) or session_id (for new standup)
      // This allows the backend to properly disassociate the news from the standup
      if (isUnfavoriting) {
        if (standupId) {
          payload.standup_id = standupId;
        } else if (sessionId) {
          payload.session_id = sessionId;
        }
      }

      const api = createApiClient();
      const response = await apiClient(api, '/stand-up/news', 'PUT', payload);

      console.log('Toggle favorite response:', response);

      if (response?.success === true) {
        let successMessage = newFavoriteStatus ? "Added to favorites" : "Removed from favorites";
        
        // If standup-specific news was unfavorited, it's now global
        if (willBeDisassociated) {
          successMessage = "News removed from this standup";
        }
        
        dispatch(
          setToastNotification({
            header: "Success",
            subtext: successMessage,
            variant: "success",
            position: "top",
            duration: 3000,
          })
        );
        return { success: true, wasDisassociated: willBeDisassociated };
      }

      throw new Error(response?.message || "Failed to update favorite status");
    } catch (error: any) {
      console.error("Error toggling favorite for news:", error);

      let errorMessage = "Failed to update favorite status. Please try again.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      dispatch(
        setToastNotification({
          header: "Error",
          subtext: errorMessage,
          variant: "error",
          position: "top",
          duration: 3000,
        })
      );

      return { success: false, wasDisassociated: false };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch news for standup context - returns standup-specific and global news
   */
  const fetchNewsForStandup = async (): Promise<{ standupNews: NewsItem[], globalNews: NewsItem[] } | null> => {
    setIsLoading(true);
    try {
      const api = createApiClient();
      
      let queryParams = `location=${encodeURIComponent(selectedLocation?.displayText || '')}`;
      if (standupId) {
        queryParams += `&standup_id=${standupId}`;
      } else if (sessionId) {
        queryParams += `&session_id=${sessionId}`;
      }

      const response = await apiClient(api, `/stand-up/news/standup?${queryParams}`, 'GET');

      if (response?.success === true && response?.data) {
        // Map API response to component format
        const mapNews = (items: any[]): NewsItem[] => items.map((item: any) => ({
          id: item.id.toString(),
          title: item.title,
          description: item.description,
          is_favourite: item.is_favourite,
          date: new Date(item.created_at).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
          createdBy: item.created_by,
          is_truncated: item.is_truncated,
          source: item.source,
          standup_id: item.standup_id,
          session_id: item.session_id,
        }));

        const standupNews = mapNews(response.data.standupNews || []);
        const globalNews = mapNews(response.data.globalNews || []);

        return {
          standupNews,
          globalNews,
        };
      }

      return { standupNews: [], globalNews: [] };
    } catch (error: any) {
      console.error('Error fetching news for standup:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { addNews, toggleNewsFavorite, fetchNewsForStandup, isLoading };
};
